import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

export const TextPair = ({ label, obj, base, set, path, testid, multiline }) => {
  const C = multiline ? Textarea : Input;
  return (
    <Field label={label}>
      <div className="grid gap-2 sm:grid-cols-2">
        {["en", "ar"].map((l) => (
          <div key={l} className="relative">
            <span className="pointer-events-none absolute end-2 top-2 rounded bg-secondary px-1 text-[10px] font-bold text-muted-foreground">{l.toUpperCase()}</span>
            <C dir={l === "ar" ? "rtl" : "ltr"} lang={l} value={obj[`${base}_${l}`] || ""} onChange={(e) => set(`${path}.${base}_${l}`, e.target.value)} rows={multiline ? 2 : undefined} maxLength={300} className="pe-10" data-testid={`${testid}-${l}`} />
          </div>
        ))}
      </div>
    </Field>
  );
};

export const ColorField = ({ label, value, onChange, testid }) => {
  const [text, setText] = useState(value);
  useEffect(() => {
    setText(value);
  }, [value]);
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 shrink-0 cursor-pointer rounded-md border bg-transparent p-0.5" data-testid={`${testid}-picker`} />
        <Input dir="ltr" value={text} maxLength={7} onChange={(e) => { setText(e.target.value); if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) onChange(e.target.value.toLowerCase()); }} className="font-mono text-xs" data-testid={`${testid}-input`} />
      </div>
    </Field>
  );
};

export const RangeField = ({ label, value, onChange, min, max, step = 1, unit = "", testid }) => (
  <Field label={<span className="flex w-full justify-between"><span>{label}</span><bdi className="tabular-nums text-foreground">{value}{unit}</bdi></span>}>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} data-testid={testid} className="py-2" />
  </Field>
);

export const ToggleField = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 rounded-lg border bg-secondary/30 px-3 py-2.5 text-sm">
    <span>{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </label>
);

export const SelectField = ({ label, value, onChange, options, testid }) => (
  <Field label={label}>
    <Select value={String(value)} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={String(v)} data-testid={`${testid}-${String(v).replace(/[^a-z0-9]/gi, "-")}`}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);

export const SegField = ({ label, value, onChange, options, testid }) => (
  <Field label={label}>
    <div className="flex flex-wrap gap-1 rounded-lg border bg-secondary/30 p-1" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={value === v} data-testid={`${testid}-${String(v).replace(/[^a-z0-9]/gi, "-")}`}
          className={`flex-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${value === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
      ))}
    </div>
  </Field>
);
